import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FiZap, FiArrowLeft, FiChevronRight, FiPackage } from "react-icons/fi";
import {
  recordProductionStep,
  fetchDailyProductionToday as apiFetchDailyProduction,
} from "../../../api/productionApi";

import { useTitle } from "../../../hooks/useTitle";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { withAuth } from "../../../components/HOC/withAuth";

import "./ProductionLines.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

function ViewProductionLines() {
  const { id } = useParams();
  const navigate = useNavigate();
  useTitle("Rovion MES - Üretim Hatları Akışı");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/production-lines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const canEditInventory =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/inventory",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const allowedMachineIds = useMemo(() => {
    const list = currentUser?.machineAccess || [];
    if (!Array.isArray(list) || list.length === 0) return null;
    return new Set(list.map((x) => String(x)));
  }, [currentUser]);

  const [lines, setLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [editedProduced, setEditedProduced] = useState({});
  const [savingLineId, setSavingLineId] = useState(null);
  const [dailyQtyByOutputId, setDailyQtyByOutputId] = useState({});
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const lastRealtimeRefreshRef = useRef(0);

  const addToast = useCallback(
    (title, descriptionOrType = "info", maybeType = "info") => {
      const knownTypes = ["success", "warning", "danger", "info"];
      const isLegacy = knownTypes.includes(descriptionOrType);
      const t = {
        id: Date.now(),
        title,
        description: isLegacy ? undefined : descriptionOrType,
        type: isLegacy ? descriptionOrType : maybeType,
      };
      setToasts((p) => [...p, t]);
      setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
    },
    [],
  );

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const h = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const fetchDailyProductionToday = useCallback(async () => {
    try {
      const map = await apiFetchDailyProduction(getToken());
      setDailyQtyByOutputId(map);
    } catch (e) {
      console.error("daily-production", e);
    }
  }, [getToken]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/production-lines/from-parts?finalOnly=true`, {
          headers: h(),
        }),
        axios.get(`${API_URL}/api/machines`, { headers: h() }),
        fetchDailyProductionToday(),
      ]);
      const [linesRes, machinesRes] = results;
      if (linesRes.status === "fulfilled") {
        const data = linesRes.value.data?.lines || linesRes.value.data || [];
        setLines(Array.isArray(data) ? data : []);
      }
      if (machinesRes.status === "fulfilled") {
        const m =
          machinesRes.value.data?.machines || machinesRes.value.data || [];
        setMachines(Array.isArray(m) ? m : []);
      }
      if (results.some((r) => r.status === "rejected")) {
        addToast("Bazı veriler yüklenemedi", "warning");
      }
    } catch (err) {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [h, fetchDailyProductionToday, addToast]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshRealtimeData = useCallback(async () => {
    const now = Date.now();
    // Prevent duplicate bursts when multiple WS messages arrive quickly.
    if (now - lastRealtimeRefreshRef.current < 500) return;
    lastRealtimeRefreshRef.current = now;

    try {
      await Promise.all([fetchDailyProductionToday()]);
    } catch (e) {
      // Keep UI stable; periodic fallback handles transient failures.
      console.error("realtime refresh error", e);
    }
  }, [fetchDailyProductionToday, getToken]);

  useEffect(() => {
    if (loading) return undefined;

    const connect = () => {
      const token = getToken();
      if (!token) return;

      const apiUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
      let wsUrl = apiUrl.replace(/^http/, "ws");
      if (!wsUrl.startsWith("ws://") && !wsUrl.startsWith("wss://")) {
        wsUrl = `ws://${wsUrl.replace(/^https?:\/\//, "")}`;
      }
      const wsPath = wsUrl.endsWith("/") ? "ws" : "/ws";

      const ws = new WebSocket(`${wsUrl}${wsPath}?token=${token}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          // Backend broadcasts this for machine/product updates.
          if (msg?.type === "telemetry" && msg?.deviceId === "machine_update") {
            refreshRealtimeData();
          }
        } catch (e) {
          // ignore malformed frames
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        reconnectTimerRef.current = setTimeout(connect, 2000);
      };
    };

    connect();

    // Keep fallback polling, but less frequent now.
    const pollId = setInterval(() => {
      refreshRealtimeData();
    }, 60000);

    return () => {
      clearInterval(pollId);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        ws.close(1000, "ViewProductionLines cleanup");
      }
      wsRef.current = null;
    };
  }, [loading, refreshRealtimeData, getToken]);

  const filteredLines = useMemo(() => {
    let result = lines || [];
    if (id) {
      result = result.filter((line) => String(line._id) === String(id));
    }
    if (!allowedMachineIds) return result;
    return result
      .map((line) => {
        const m = (line.machineItems || []).filter((mi) => {
          const mid = mi.machineId?._id || mi.machineId;
          return mid && allowedMachineIds.has(String(mid));
        });
        return { ...line, machineItems: m };
      })
      .filter((line) => (line.machineItems || []).length > 0);
  }, [lines, id, allowedMachineIds]);

  const getMachineLabel = (mi) => {
    const m = mi.machineId || {};
    if (m.name && m.code) return `${m.name} (${m.code})`;
    return m.name || m.code || "Makine";
  };

  const formatLineItemsInputs = (mi, showStock = true) => {
    const items = mi.lineItems || [];
    if (items.length === 0) {
      const p = mi.entryPartId;
      if (p)
        return `${p.name || p.code || "Parça"} × ${Number(mi.inputAmount) || 1}`;
      return "—";
    }
    return items
      .map((li) => {
        const part = li.partId;
        const qty = Number(li.quantityUsed) || 0;
        const label = part
          ? part.name && part.code
            ? `${part.name} (${part.code})`
            : part.name || part.code || "Parça"
          : "Parça";
        return `${label} × ${qty}`;
      })
      .filter(Boolean)
      .join(", ");
  };

  const getProducedForMachine = (line, idx) => {
    const key = `${line._id}-${idx}`;
    const mi = (line.machineItems || [])[idx];
    const fromServer = mi?.producedCount != null ? Number(mi.producedCount) : 0;
    const base = editedProduced[key] != null ? editedProduced[key] : fromServer;
    // this input represents "produce this many now" (not a running total)
    return Math.max(0, parseInt(base, 10) || 0);
  };

  const getTodayRecordedForStep = (mi) => {
    const oid = mi.outputItemId ? String(mi.outputItemId) : null;
    if (!oid) return 0;
    return dailyQtyByOutputId[oid] ?? 0;
  };

  const handleProducedChange = (lineId, idx, val) => {
    setEditedProduced((p) => ({ ...p, [`${lineId}-${idx}`]: val }));
  };

  const getInputPerOutput = (mi) => {
    const ipo = Number(mi?.inputPerOutput);
    if (!Number.isNaN(ipo) && ipo > 0) return ipo;
    const inAmt = Number(mi?.inputAmount);
    const outAmt = Number(mi?.outputAmount);
    if (
      !Number.isNaN(inAmt) &&
      !Number.isNaN(outAmt) &&
      inAmt > 0 &&
      outAmt > 0
    )
      return inAmt / outAmt;
    return 1;
  };

  const getRatioLabel = (mi) => {
    const inAmt = Number(mi?.inputAmount);
    const outAmt = Number(mi?.outputAmount);
    if (
      !Number.isNaN(inAmt) &&
      !Number.isNaN(outAmt) &&
      inAmt > 0 &&
      outAmt > 0
    )
      return `${inAmt} → ${outAmt}`;
    const ipo = getInputPerOutput(mi);
    return ipo === 1 ? "1 → 1" : `${ipo} giriş / 1 çıkış`;
  };

  const saveProducedCounts = async (line) => {
    const machineList = line.machineItems || [];
    const entries = machineList
      .map((_, idx) => ({ idx, qty: getProducedForMachine(line, idx) }))
      .filter((x) => (Number(x.qty) || 0) > 0);

    if (entries.length === 0) {
      addToast("Kayıt yok", "Üretilen miktar 0 olamaz", "warning");
      return;
    }

    // Optimistic UI: apply counts immediately
    const lineId = line.id || `part:${line._id}`;
    setDailyQtyByOutputId((prev) => {
      const next = { ...prev };
      entries.forEach((e) => {
        const mi = machineList[e.idx];
        const oid = mi?.outputItemId ? String(mi.outputItemId) : null;
        if (oid) next[oid] = (next[oid] || 0) + Number(e.qty);
      });
      return next;
    });

    setSavingLineId(line._id);
    try {
      await Promise.all(
        entries.map((e) =>
          recordProductionStep(getToken(), {
            lineId,
            stepIndex: e.idx,
            producedNow: e.qty,
          }),
        ),
      );
      setEditedProduced((p) => {
        const next = { ...p };
        entries.forEach((e) => delete next[`${line._id}-${e.idx}`]);
        return next;
      });
      // Refresh to get server-authoritative totals
      await fetchDailyProductionToday();
      addToast("Kaydedildi", "success");
    } catch (err) {
      // Revert optimistic update on failure
      await fetchDailyProductionToday();
      addToast(
        "Hata",
        err.response?.data?.message || "Kaydetme başarısız",
        "danger",
      );
    } finally {
      setSavingLineId(null);
    }
  };

  return (
    <AppPage
      title="Üretim Hatları Akışı"
      subtitle="Tüm hatların makine sırası"
      icon={<FiZap />}
      actions={
        <Button
          appearance="danger-subtle"
          onClick={() => navigate("/production-lines")}
          icon={<FiArrowLeft />}
        >
          Geri
        </Button>
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            {/* <div className="pl-analytics">
              <h5 className="pl-analytics-title">
                <FiBarChart2 /> Analiz
              </h5>

              <div className="pl-analytics-grid">
                <div className="pl-analytics-card pl-analytics-card--full">
                  <div className="pl-analytics-card__header">
                    <h6>Model Bazında Üretim Kapasitesi</h6>
                    <div className="pl-model-select-wrap">
                      <Select
                        options={models.map((m) => ({
                          value: m._id,
                          label: `${m.name} (${m.code})`,
                        }))}
                        selected={selectedModelId ? [selectedModelId] : []}
                        onSelected={(values) =>
                          setSelectedModelId(values?.[0]?.value || "")
                        }
                        unique
                      />
                    </div>
                  </div>

                  <div className="pl-analytics-card__body">
                    {selectedModelId &&
                      (() => {
                        const a = modelAnalytics.find(
                          (x) => x.model._id === selectedModelId,
                        );
                        if (!a)
                          return <p className="text-muted">Model bulunamadı</p>;
                        return (
                          <div className="pl-model-detail">
                            <div className="pl-model-stats">
                              {a.maxProducible != null && (
                                <>
                                  <span className="pl-model-max">
                                    Stokla üretilebilecek: {a.maxProducible}{" "}
                                    adet
                                  </span>
                                  {a.maxProducible === 0 && (
                                    <span className="pl-model-shortage">
                                      Stok yetersiz — üretilemez
                                    </span>
                                  )}
                                  {a.bottleneck && (
                                    <span
                                      className="pl-model-bottleneck"
                                      title="Darboğaz kalem"
                                    >
                                      <FiAlertTriangle /> {a.bottleneck}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="pl-model-usage">
                              <button
                                type="button"
                                className="pl-model-usage-toggle"
                                onClick={() => setShowModelUsage((v) => !v)}
                              >
                                <span>1 adet için kullanılacak envanter</span>
                                <span className="pl-model-usage-toggle-icon">
                                  {showModelUsage ? "▾" : "▸"}
                                </span>
                              </button>
                              {showModelUsage && (
                                <>
                                  {(a.model.usedInventory || []).length ? (
                                    <ul>
                                      {(a.model.usedInventory || []).map(
                                        (u, i) => {
                                          const invId =
                                            u.inventoryItemId?._id ||
                                            u.inventoryItemId;
                                          const inv = invMap[invId];
                                          const need = Number(u.quantity) || 0;
                                          const stock = inv
                                            ? Number(inv.quantity) || 0
                                            : 0;
                                          return (
                                            <li key={i}>
                                              {getInvName(invId)} × {need}
                                              <span className="pl-model-usage-stock">
                                                (stok: {stock})
                                              </span>
                                            </li>
                                          );
                                        },
                                      )}
                                    </ul>
                                  ) : (
                                    <p className="text-muted">
                                      BOM tanımlı değil
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    {!selectedModelId && (
                      <p className="text-muted">
                        Görüntülemek için model seçin
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div> */}

            <div className="pl-visual">
              {filteredLines.length === 0 && (
                <div className="empty-state">
                  <FiZap size={64} />
                  <p>Tanımlı üretim hattı yok</p>
                </div>
              )}

              {filteredLines.map((line) => {
                const machines = line.machineItems || [];
                const totalProduced = machines.reduce(
                  (s, _, i) => s + getProducedForMachine(line, i),
                  0,
                );
                const isSaving = savingLineId === line._id;

                return (
                  <div
                    key={line._id}
                    className={`pl-lane pl-lane--steps ${id === line._id ? "pl-lane--active" : ""}`}
                  >
                    <div className="pl-lane-header">
                      <span className="pl-name">{line.name}</span>
                      <span className="pl-code">{line.code}</span>
                      <span className="pl-total-badge">
                        Toplam: {totalProduced} adet
                      </span>
                      <span
                        className={`pl-badge pl-type-${line.productLineType || "production"}`}
                      >
                        {(line.productLineType || "production") === "selling"
                          ? "Sipariş"
                          : "Üretim"}
                      </span>
                      <span
                        className={`pl-badge pl-entry-${line.dataEntryType || "manual"}`}
                      >
                        {(line.dataEntryType || "manual") === "from_machine"
                          ? "Makineden"
                          : "Manuel"}
                      </span>
                    </div>

                    <div className="pl-steps">
                      <div className="pl-steps-pipeline">
                        {machines.map((mi, idx) => (
                          <React.Fragment key={idx}>
                            <div className="pl-step-card">
                              <div className="pl-step-card-head">
                                <span className="pl-step-num">{idx + 1}</span>
                                <span className="pl-step-machine">
                                  {getMachineLabel(mi)}
                                </span>
                              </div>
                              <div className="pl-step-card-body">
                                <label className="pl-step-label">
                                  Üretilen
                                </label>
                                <div className="pl-step-input-wrap">
                                  <input
                                    type="number"
                                    min={0}
                                    className="pl-produced-input"
                                    disabled={!canWrite}
                                    value={
                                      editedProduced[`${line._id}-${idx}`] ??
                                      (getProducedForMachine(line, idx) || "")
                                    }
                                    onChange={(e) =>
                                      handleProducedChange(
                                        line._id,
                                        idx,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <span className="pl-step-unit">adet</span>
                                </div>
                                <div className="pl-step-today-recorded text-muted small mt-1">
                                  Bugün kayıtlı: {getTodayRecordedForStep(mi)}{" "}
                                  adet
                                </div>
                                <div className="pl-step-continuity mt-2">
                                  <div className="pl-step-row">
                                    <span className="pl-step-sublabel">
                                      Girdiler
                                    </span>
                                    <span className="pl-step-continuity-value">
                                      {formatLineItemsInputs(mi)}
                                    </span>
                                  </div>
                                  <div className="pl-step-row">
                                    <span className="pl-step-sublabel">
                                      Çıktı
                                    </span>
                                    <span className="pl-step-continuity-value">
                                      {(() => {
                                        const p = mi.outputPartId;
                                        if (!p) return "—";
                                        return p.name && p.code
                                          ? `${p.name} (${p.code})`
                                          : p.name || p.code || "Parça";
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {idx < machines.length - 1 && (
                              <div className="pl-step-connector">
                                <FiChevronRight />
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                        {machines.length > 0 &&
                          (() => {
                            const p = line?.outputPartId;
                            if (!p) return null;
                            return (
                              <>
                                <div className="pl-step-connector">
                                  <FiChevronRight />
                                </div>
                                <div className="pl-step-card pl-step-card--final">
                                  <div className="pl-step-card-head pl-step-card-head--final">
                                    <span className="pl-step-num pl-step-num--final">
                                      <FiPackage />
                                    </span>
                                    <span className="pl-step-machine">
                                      Son Ürün
                                    </span>
                                  </div>
                                  <div className="pl-step-card-body">
                                    <span className="pl-final-name">
                                      {p.name}
                                    </span>
                                    <span className="pl-final-code">
                                      {p.code}
                                    </span>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                      </div>
                      {canWrite && (
                        <div className="pl-step-actions">
                          <Button
                            appearance="primary-animated"
                            onClick={() => saveProducedCounts(line)}
                            disabled={isSaving}
                          >
                            {isSaving ? "Kaydediliyor..." : "Kaydet"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            description={t.description}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
}

export default withAuth(ViewProductionLines, ["admin", "employee", "user"]);
